import { useState, useMemo } from "react";

// ─────────────────────────────────────────────
// AUTO LOAN CALCULATOR
// ─────────────────────────────────────────────

function calcLoan(principal, termMonths, apr, lumpSum, lumpFreq) {
  const r = apr / 100 / 12;
  const monthlyPayment =
    (principal * r * Math.pow(1 + r, termMonths)) /
    (Math.pow(1 + r, termMonths) - 1);
  let balance = principal,
    totalInterest = 0,
    month = 0;
  const balanceHistory = [principal];
  while (balance > 0.005 && month < termMonths + 120) {
    month++;
    const interest = balance * r;
    totalInterest += interest;
    balance -= Math.min(monthlyPayment - interest, balance);
    if (month % lumpFreq === 0 && balance > 0)
      balance -= Math.min(lumpSum, balance);
    balance = Math.max(0, balance);
    balanceHistory.push(balance);
    if (balance === 0) break;
  }
  return {
    monthlyPayment,
    actualMonths: month,
    totalInterest,
    totalPaid: principal + totalInterest,
    savedMonths: termMonths - month,
    balanceHistory,
  };
}
function calcNoLump(principal, termMonths, apr) {
  const r = apr / 100 / 12;
  const mp =
    (principal * r * Math.pow(1 + r, termMonths)) /
    (Math.pow(1 + r, termMonths) - 1);
  return { totalInterest: mp * termMonths - principal };
}
function fmt(n) {
  return "$" + Math.round(n).toLocaleString("en-US");
}
function fmtD(n) {
  return (
    "$" +
    n.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

function PayoffChart({ history, principal }) {
  const w = 400,
    h = 90;
  if (history.length < 2) return null;
  const pts = history.map(
    (b, i) =>
      `${(i / (history.length - 1)) * w},${h - (b / principal) * (h - 2) - 1}`,
  );
  return (
    <svg
      width="100%"
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      style={{ display: "block", overflow: "visible" }}
    >
      <defs>
        <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#00D9A3" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#00D9A3" stopOpacity="0.01" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${pts.join(" ")} ${w},${h}`} fill="url(#cg)" />
      <polyline
        points={pts.join(" ")}
        fill="none"
        stroke="#00D9A3"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={pts[pts.length - 1].split(",")[0]}
        cy={h - 1}
        r="4"
        fill="#00D9A3"
      />
    </svg>
  );
}
function LoanSlider({
  label,
  value,
  display,
  min,
  max,
  step,
  onChange,
  color,
}) {
  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: "#4A4A62",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontFamily: "'DM Mono', monospace",
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize: 13,
            color: color || "#00D9A3",
            fontFamily: "'DM Mono', monospace",
            fontWeight: 600,
          }}
        >
          {display}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          width: "100%",
          accentColor: color || "#00D9A3",
          cursor: "pointer",
        }}
      />
    </div>
  );
}
function Tile({ label, value, accent, full, sub }) {
  return (
    <div
      style={{
        background: "rgba(0,0,0,0.3)",
        borderRadius: 10,
        padding: "12px 14px",
        gridColumn: full ? "1 / -1" : undefined,
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: "#383850",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          marginBottom: 4,
          fontFamily: "'DM Mono', monospace",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: accent || "#ccc" }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: "#333348", marginTop: 2 }}>
          {sub}
        </div>
      )}
    </div>
  );
}
function AutoLoanCalculator() {
  const [principal, setPrincipal] = useState(20000);
  const [term, setTerm] = useState(65);
  const [apr, setApr] = useState(4.99);
  const [lumpSum, setLumpSum] = useState(2000);
  const [lumpFreq, setLumpFreq] = useState(3);
  const result = useMemo(
    () => calcLoan(principal, term, apr, lumpSum, lumpFreq),
    [principal, term, apr, lumpSum, lumpFreq],
  );
  const baseline = useMemo(
    () => calcNoLump(principal, term, apr),
    [principal, term, apr],
  );
  const interestPct = ((result.totalInterest / principal) * 100).toFixed(1);
  const interestSaved = baseline.totalInterest - result.totalInterest;
  const paidEarly = result.savedMonths > 0;
  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        background: "#08080F",
        color: "#E0E0EC",
        fontFamily: "'Sora', sans-serif",
        padding: "36px 32px 64px",
      }}
    >
      <div style={{ maxWidth: 520, margin: "0 auto" }}>
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 10,
              letterSpacing: "0.2em",
              color: "#252535",
              textTransform: "uppercase",
              marginBottom: 10,
            }}
          >
            Auto Loan · Payoff Simulator
          </div>
          <h1
            style={{
              margin: 0,
              fontSize: "clamp(24px, 4vw, 36px)",
              fontWeight: 800,
              letterSpacing: "-0.03em",
              lineHeight: 1.1,
            }}
          >
            Loan <span style={{ color: "#00D9A3" }}>Payoff</span> Calculator
          </h1>
          <p
            style={{
              color: "#303048",
              fontSize: 13,
              marginTop: 8,
              marginBottom: 0,
            }}
          >
            Drag sliders — results update instantly
          </p>
        </div>
        <div
          style={{
            background: "rgba(255,255,255,0.022)",
            border: "1px solid rgba(255,255,255,0.055)",
            borderRadius: 16,
            padding: "22px 22px 18px",
            marginBottom: 18,
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >
          <LoanSlider
            label="Loan Amount"
            value={principal}
            display={`$${principal.toLocaleString()}`}
            min={5000}
            max={50000}
            step={500}
            onChange={setPrincipal}
            color="#00D9A3"
          />
          <LoanSlider
            label="Loan Term"
            value={term}
            display={`${term} months`}
            min={12}
            max={84}
            step={1}
            onChange={setTerm}
            color="#5B9BFF"
          />
          <LoanSlider
            label="Interest Rate (APR)"
            value={apr}
            display={`${apr.toFixed(2)}%`}
            min={1}
            max={15}
            step={0.25}
            onChange={setApr}
            color="#FF7C5B"
          />
          <LoanSlider
            label="Lump Sum Payment"
            value={lumpSum}
            display={lumpSum === 0 ? "None" : `$${lumpSum.toLocaleString()}`}
            min={0}
            max={5000}
            step={250}
            onChange={setLumpSum}
            color="#00D9A3"
          />
          <LoanSlider
            label="Lump Sum Every"
            value={lumpFreq}
            display={`${lumpFreq} months`}
            min={1}
            max={12}
            step={1}
            onChange={setLumpFreq}
            color="#00D9A3"
          />
        </div>
        <div
          style={{
            background: "rgba(0,217,163,0.055)",
            border: "1.5px solid rgba(0,217,163,0.2)",
            borderRadius: 16,
            padding: "24px 22px",
            boxShadow: "0 0 48px rgba(0,217,163,0.07)",
            marginBottom: 14,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 20,
            }}
          >
            <div>
              <div style={{ fontSize: 12, color: "#2A5045", marginBottom: 3 }}>
                Loan term
              </div>
              <div
                style={{
                  fontSize: 34,
                  fontWeight: 800,
                  color: "#00D9A3",
                  lineHeight: 1,
                }}
              >
                {term}{" "}
                <span
                  style={{ fontSize: 16, fontWeight: 400, color: "#1E4A3A" }}
                >
                  months
                </span>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, color: "#2A5045", marginBottom: 3 }}>
                Required monthly
              </div>
              <div
                style={{
                  fontSize: 26,
                  fontWeight: 700,
                  color: "#fff",
                  lineHeight: 1,
                }}
              >
                {fmtD(result.monthlyPayment)}
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <div
              style={{
                fontSize: 10,
                color: "#1A3A30",
                fontFamily: "'DM Mono', monospace",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              Remaining balance
            </div>
            <PayoffChart
              history={result.balanceHistory}
              principal={principal}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 5,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  color: "#1A2A24",
                  fontFamily: "'DM Mono', monospace",
                }}
              >
                Month 0 · {fmt(principal)}
              </span>
              <span
                style={{
                  fontSize: 10,
                  color: "#00D9A3",
                  fontFamily: "'DM Mono', monospace",
                }}
              >
                Paid off month {result.actualMonths}
              </span>
            </div>
          </div>
          <div
            style={{
              height: 1,
              background: "rgba(0,217,163,0.1)",
              margin: "18px 0",
            }}
          />
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}
          >
            <Tile
              label="Paid off in"
              value={`${result.actualMonths} months`}
              accent="#00D9A3"
            />
            <Tile
              label={paidEarly ? "Early by" : "On schedule"}
              value={paidEarly ? `${result.savedMonths} months` : "—"}
              accent={paidEarly ? "#00D9A3" : "#333348"}
            />
            <Tile
              label="Total interest paid"
              value={fmt(result.totalInterest)}
              sub={`${interestPct}% of loan`}
            />
            <Tile
              label="Interest saved"
              value={lumpSum > 0 ? fmt(Math.max(0, interestSaved)) : "—"}
              accent={lumpSum > 0 && interestSaved > 0 ? "#00D9A3" : "#333348"}
              sub={lumpSum > 0 ? "vs no lump sums" : "add lump sums above"}
            />
            <Tile
              label="Total amount paid"
              value={fmt(result.totalPaid)}
              full
            />
          </div>
        </div>
        {paidEarly && lumpSum > 0 && (
          <div
            style={{
              background: "rgba(91,155,255,0.05)",
              border: "1px solid rgba(91,155,255,0.13)",
              borderRadius: 12,
              padding: "13px 17px",
              display: "flex",
              gap: 11,
              alignItems: "flex-start",
            }}
          >
            <span style={{ fontSize: 18, lineHeight: 1, marginTop: 1 }}>
              ⚡
            </span>
            <div style={{ fontSize: 13, color: "#4A5A7A", lineHeight: 1.6 }}>
              Your{" "}
              <strong style={{ color: "#8AABEE" }}>
                ${lumpSum.toLocaleString()}
              </strong>{" "}
              every{" "}
              <strong style={{ color: "#8AABEE" }}>{lumpFreq} months</strong>{" "}
              pays off this loan{" "}
              <strong style={{ color: "#fff" }}>
                {result.savedMonths} months early
              </strong>{" "}
              and saves{" "}
              <strong style={{ color: "#fff" }}>
                {fmt(Math.max(0, interestSaved))}
              </strong>{" "}
              in interest.
            </div>
          </div>
        )}
        <div
          style={{
            marginTop: 18,
            fontSize: 10,
            color: "#181828",
            textAlign: "center",
            fontFamily: "'DM Mono', monospace",
          }}
        >
          Lump sums at month-end · {apr.toFixed(2)}% APR compounded monthly · No
          prepayment penalty assumed
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// CCA STUDY PLAN
// ─────────────────────────────────────────────

// Renders a study-plan item: plain string, or { text, url, pre, post } for a link.
// An optional `challenge: { teaser, spec, hint }` renders a collapsible badge.
function ResourceItem({ item, accent }) {
  const [open, setOpen] = useState(false);

  const linkStyle = {
    color: accent || "#8B6F47",
    textDecoration: "underline",
    textDecorationColor: "rgba(139,111,71,0.4)",
    textUnderlineOffset: "2px",
    cursor: "pointer",
  };

  const label =
    typeof item === "string" ? (
      item
    ) : (
      <>
        {item.pre && <>{item.pre}</>}
        {item.url ? (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
          >
            {item.text}
          </a>
        ) : (
          item.text
        )}
        {item.post && <>{item.post}</>}
      </>
    );

  const challenge = typeof item === "object" ? item.challenge : null;

  return (
    <>
      {label}
      {challenge && (
        <span style={{ display: "block", marginTop: 5 }}>
          <button
            onClick={() => setOpen((o) => !o)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              background: open ? accent || "#8B6F47" : "rgba(0,0,0,0.06)",
              border: `1px solid ${open ? accent || "#8B6F47" : "rgba(0,0,0,0.1)"}`,
              borderRadius: 20,
              padding: "2px 9px",
              cursor: "pointer",
              fontSize: 10.5,
              fontFamily: "inherit",
              color: open ? "#fff" : "#5A4A36",
              fontWeight: 600,
              letterSpacing: "0.02em",
              transition: "all 0.15s",
            }}
          >
            <span>💻</span>
            <span>Try this</span>
            <span style={{ fontSize: 9, opacity: 0.7 }}>
              {open ? "▲" : "▼"}
            </span>
          </button>
          {open && (
            <span
              style={{
                display: "block",
                marginTop: 6,
                borderRadius: "0 6px 6px 6px",
                overflow: "hidden",
                border: `1px solid ${accent ? accent + "55" : "#C8B89055"}`,
              }}
            >
              <span
                style={{
                  display: "block",
                  padding: "8px 12px",
                  background: accent ? accent + "18" : "rgba(139,111,71,0.08)",
                  fontSize: 11.5,
                  color: "#2C2416",
                  lineHeight: 1.55,
                  fontStyle: "italic",
                }}
              >
                {challenge.teaser}
              </span>
              <span
                style={{
                  display: "block",
                  padding: "8px 12px",
                  background: "rgba(0,0,0,0.02)",
                  fontSize: 11,
                  color: "#4A3A2A",
                  lineHeight: 1.6,
                  borderTop: `1px solid ${accent ? accent + "30" : "rgba(139,111,71,0.15)"}`,
                }}
              >
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: 10,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: accent || "#8B6F47",
                    marginRight: 5,
                  }}
                >
                  Spec
                </span>
                {challenge.spec}
              </span>
              <span
                style={{
                  display: "block",
                  padding: "7px 12px",
                  background: "rgba(0,0,0,0.03)",
                  fontSize: 10.5,
                  color: "#6A5A4A",
                  lineHeight: 1.55,
                  borderTop: `1px solid ${accent ? accent + "22" : "rgba(139,111,71,0.1)"}`,
                }}
              >
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: 10,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: accent || "#8B6F47",
                    marginRight: 5,
                  }}
                >
                  💡 Hint
                </span>
                {challenge.hint}
              </span>
            </span>
          )}
        </span>
      )}
    </>
  );
}

const phases = [
  {
    id: 1,
    title: "Foundation",
    subtitle: "Weeks 1–4",
    duration: "~50 hrs",
    color: "#C8B8A2",
    accent: "#8B6F47",
    icon: "◎",
    goal: "Understand Claude's API mechanics and the exam blueprint",
    domains: ["Prompt Engineering 20%", "Context Management 15%"],
    weeks: [
      {
        week: "Weeks 1–2",
        focus: "Claude API Fundamentals",
        theory: [
          {
            url: "https://anthropic.skilljar.com/claude-101",
            text: "Anthropic Skilljar: Claude 101",
            post: " (free, ~1hr)",
          },
          {
            url: "https://anthropic.skilljar.com/claude-with-the-anthropic-api",
            text: "Skilljar: Building with the Claude API",
            post: " – Part 1 (Messages API, roles, stop_reason, streaming)",
          },
          {
            url: "https://docs.anthropic.com/en/api/messages",
            text: "Read: platform.claude.com/docs → Messages API reference",
          },
          {
            url: "https://docs.anthropic.com/en/docs/about-claude/models/overview",
            text: "Read: Models Overview",
            post: " (Opus 4.7 / Sonnet 4.6 / Haiku 4.5)",
          },
        ],
        hands_on: [
          {
            text: "Set up anthropic-java SDK 2.26.0 via Maven",
            challenge: {
              teaser:
                "Wire up your first Maven project and get a real response from Claude in Java.",
              spec: "Create a new Maven project, add anthropic-java 2.26.0, write a main() that sends 'Say hello in 5 words' and prints the response text. Assert stop_reason == end_turn and the response contains exactly 5 words.",
              hint: "Use Anthropic.builder().build() to get a client — no API key config needed if ANTHROPIC_API_KEY is set as an env var. The response text lives at message.content().get(0).text().",
            },
          },
          {
            text: "Make your first sync + async Messages API call in Java",
            challenge: {
              teaser:
                "Send the same prompt two ways and compare results and timing.",
              spec: "Call 'List 3 planet names' both synchronously and via CompletableFuture. Assert both return identical content. Log wall-clock time for each — async should not block the calling thread.",
              hint: "Use client.messages().createAsync(...) which returns a CompletableFuture<Message>. Call .get() to block for the result in your test, but time the gap between submission and get().",
            },
          },
          {
            text: "Experiment with system prompts, temperature, max_tokens",
            challenge: {
              teaser:
                "See how a system prompt and temperature knob change Claude's output.",
              spec: "System prompt: 'Reply only in JSON'. Ask 'What is the capital of France?' at temperature 0 and 1.0 (5 runs each). Verify every temp-0 response parses as valid JSON; log how many temp-1.0 runs deviate.",
              hint: "Wrap JSON.parse() in a try/catch and count parse errors across runs. At temperature 0, you should see 0 failures; at 1.0 expect occasional extra prose around the JSON.",
            },
          },
          {
            text: "Try streaming with MessageAccumulator",
            challenge: {
              teaser:
                "Print tokens as they arrive, then reconstruct the full text.",
              spec: "Stream 'Write a 200-word story'. Print each text delta as it arrives. After the stream ends, use MessageAccumulator.accumulate() to get the full message and assert its word count is ≥ 180.",
              hint: "Register a StreamingMessageHandler and accumulate chunks manually, or use the SDK's built-in accumulator. Split on whitespace to count words — don't use a word-count library.",
            },
          },
        ],
        milestone:
          "Can call Claude from Java and explain roles, stop_reason, and streaming mechanics",
      },
      {
        week: "Weeks 3–4",
        focus: "Prompt Engineering & Structured Output",
        theory: [
          {
            url: "https://anthropic.skilljar.com/claude-with-the-anthropic-api",
            text: "Skilljar: Prompt Engineering for Claude",
            post: " (covers XML tags, CoT, few-shot)",
          },
          {
            url: "https://docs.anthropic.com/en/docs/build-with-claude/prompt-engineering/overview",
            text: "Read: Anthropic Docs → Prompt Engineering Overview",
          },
          {
            url: "https://docs.anthropic.com/en/docs/build-with-claude/structured-outputs",
            text: "Read: Structured Outputs (beta) docs",
          },
          "Study: Anti-patterns — jailbreaks, prompt leaks, hallucination patterns",
        ],
        hands_on: [
          {
            text: "Build a Java app that extracts structured JSON from unstructured text",
            challenge: {
              teaser: "Turn a messy receipt string into a typed Java object.",
              spec: "Input: 'Alice paid $42.50 for 3 items at Whole Foods on 2024-03-15'. Prompt Claude to return JSON with keys merchant, amount, item_count, date. Write a JUnit test that deserializes via Jackson and asserts all four fields are non-null and amount == 42.50.",
              hint: "Use XML tags in your prompt (<receipt>…</receipt>) to wrap the input — it makes extraction more reliable. Tell Claude 'respond only with JSON, no prose' in the system prompt.",
            },
          },
          {
            text: "Practice chain-of-thought prompting and measure output quality",
            challenge: {
              teaser:
                "Measure how much 'think step by step' actually helps on a tricky counting task.",
              spec: "Ask 'How many r's in strawberry?' bare vs. with chain-of-thought prefix over 5 runs each. Log accuracy (correct answer: 3). Expect CoT to win — document the exact failure mode you observe without it.",
              hint: "The bare prompt often returns 2 because Claude miscounts doubled letters. In the CoT version, Claude should spell out s-t-r-a-w-b-e-r-r-y and find the r at positions 3, 8, 9.",
            },
          },
          {
            text: "Try few-shot vs zero-shot on a classification task",
            challenge: {
              teaser:
                "Quantify the accuracy lift from adding labeled examples.",
              spec: "Classify 10 customer messages as Bug, Feature Request, or Billing. Run zero-shot first, then add 3 labeled examples per class. Score both against a gold-label set you create. Expect ≥ 20% accuracy lift from few-shot.",
              hint: "Put your examples inside <examples>…</examples> XML tags. Order matters — put the most representative example last so it's closest to the query in the prompt.",
            },
          },
          {
            url: "https://github.com/anthropics/claude-cookbooks",
            text: "Anthropic Cookbook",
            post: ": tool_use/structured_outputs notebooks (translate to Java)",
            challenge: {
              teaser:
                "Port the Python structured-output notebook to a generic Java extractor.",
              spec: "Write StructuredExtractor<T> that accepts a POJO class and raw text, builds the prompt dynamically from the class fields, calls Claude, and deserializes the response via Jackson. Add a retry (max 2) if Claude returns malformed JSON.",
              hint: "Use Jackson's ObjectMapper.getSerializationConfig().introspect() to list the field names of T at runtime. On retry, add 'Your previous response was not valid JSON. Try again.' to the message history.",
            },
          },
        ],
        milestone:
          "Can engineer prompts that produce reliable structured output from Claude",
      },
    ],
  },
  {
    id: 2,
    title: "Tool Use & MCP",
    subtitle: "Weeks 5–8",
    duration: "~55 hrs",
    color: "#B8C8B8",
    accent: "#4A7A4A",
    icon: "⬡",
    goal: "Master tool definitions, MCP servers, and Java-based tool integrations",
    domains: ["Tool Design & MCP 18%", "Prompt Engineering 20%"],
    weeks: [
      {
        week: "Weeks 5–6",
        focus: "Tool Use Deep Dive",
        theory: [
          {
            url: "https://anthropic.skilljar.com/introduction-to-model-context-protocol",
            text: "Skilljar: Introduction to MCP",
            post: " (~1.5 hrs)",
          },
          {
            url: "https://docs.anthropic.com/en/docs/build-with-claude/tool-use/overview",
            text: "Read: Anthropic Docs → Tool Use full section",
          },
          {
            url: "https://docs.anthropic.com/en/docs/build-with-claude/tool-use/tool-use-examples",
            text: "Read: tool_choice modes",
            post: " (auto, any, specific tool)",
          },
          "Study: Parallel tool calls, tool descriptions as the key selection signal",
        ],
        hands_on: [
          {
            text: "Build a Java tool-calling loop: define 3 tools as JSON Schema, handle tool_use stop_reason",
            challenge: {
              teaser:
                "Build the full request→tool_use→result→answer cycle with 3 real tools.",
              spec: "Define get_weather(city), get_stock_price(ticker), and search_web(query) as JSON Schema tools. Ask 'What's the weather in Tokyo and the AAPL price?' — your loop must detect tool_use, dispatch both calls, inject results, and return a natural-language answer.",
              hint: "Check stop_reason after each API call. When it's 'tool_use', iterate content blocks for type=='tool_use' items. Build a ToolResult for each and append them as a new 'user' message before calling Claude again.",
            },
          },
          {
            text: "Implement parallel tool execution with CompletableFuture",
            challenge: {
              teaser: "Fan out all tool calls at once instead of one-by-one.",
              spec: "When Claude requests 3+ tool calls in a single turn, execute all via CompletableFuture.allOf(). Measure total latency parallel vs sequential on a prompt that reliably triggers 3 simultaneous calls. Target ≥ 2× speedup.",
              hint: "Map each tool_use block to a CompletableFuture<ToolResult> using your mock tool executor, then CompletableFuture.allOf(futures).join(). Collect results in the same order as the original tool_use blocks.",
            },
          },
          {
            url: "https://github.com/anthropics/claude-cookbooks/tree/main/tool_use",
            text: "Anthropic Cookbook",
            post: ": tool_use/calculator_tool + database_schema notebooks",
            challenge: {
              teaser:
                "Translate the Python calculator tool to Java with proper error handling.",
              spec: "Implement add, subtract, multiply, divide as a single calculator tool with an operation enum. Division by zero must return an isError:true ToolResult — not throw. Claude should read the error and explain it to the user in plain English.",
              hint: "Return {isError: true, content: [{type: 'text', text: 'Division by zero is undefined'}]} in your ToolResult. Do not throw a Java exception — Claude only sees the tool result, not your stack trace.",
            },
          },
          {
            text: "Practice: write tool descriptions that guide Claude to the right tool",
            challenge: {
              teaser:
                "Prove that description wording controls which tool Claude picks.",
              spec: "Create lookup_user_by_email and lookup_user_by_id with carefully worded descriptions. Run 10 prompts alternating email vs id input. Goal: 0 cross-selections. If you see any, tighten the description until all 10 route correctly.",
              hint: "Be explicit in descriptions: 'Use this tool ONLY when the input is an email address containing @' vs 'Use this tool ONLY when given a numeric user ID'. Vague descriptions cause Claude to guess.",
            },
          },
        ],
        milestone:
          "Can build a multi-tool agentic loop in Java that handles parallel calls correctly",
      },
      {
        week: "Weeks 7–8",
        focus: "MCP Integration",
        theory: [
          {
            url: "https://anthropic.skilljar.com/model-context-protocol-advanced-topics",
            text: "Skilljar: MCP Advanced Topics",
          },
          {
            url: "https://modelcontextprotocol.io/docs/concepts/tools",
            text: "Read: modelcontextprotocol.io – tools / resources / prompts spec",
          },
          {
            url: "https://docs.anthropic.com/en/docs/claude-code/settings",
            text: "Read: .mcp.json vs ~/.claude.json, env-var substitution",
          },
          "Study: Structured isError retry semantics, remote MCP servers",
        ],
        hands_on: [
          {
            text: "Stand up a local MCP server (use the MCP Java SDK)",
            challenge: {
              teaser:
                "Build a file-system MCP server with a security boundary.",
              spec: "Implement read_file(path) and list_dir(path) using the MCP Java SDK. The server must reject any path outside /tmp with isError:true. Write a unit test: a path-traversal attempt (../../etc/passwd) must return the error, not file contents.",
              hint: "Use Path.toRealPath() and check startsWith(allowedRoot) after resolving symlinks. Reject before calling any I/O — the check must happen in your tool handler, not a filter.",
            },
          },
          {
            text: "Connect it to Claude and call it from your Java app",
            challenge: {
              teaser:
                "Chain MCP server → Claude → final answer in one round trip.",
              spec: "Connect your MCP server to a Claude client session. Send 'List files in /tmp and read the first .txt file.' Confirm Claude issues list_dir then read_file, and the final response contains the file contents verbatim.",
              hint: "Use the MCP client's tools/list endpoint to register your server's tools with Claude's tool definitions. Claude will call them by name — your loop handles dispatching to the MCP server.",
            },
          },
          {
            text: "Practice error handling: malformed tool results, retry logic",
            challenge: {
              teaser: "Make your tool loop resilient to flaky tools.",
              spec: "Simulate 30% failure rate: randomly return isError:true with 'Service unavailable.' Write a RetryingToolExecutor that retries up to 3 times with exponential backoff. Log retry count per call. Assert success rate ≥ 95% over 50 runs.",
              hint: "Use ThreadLocalRandom.current().nextDouble() < 0.3 to simulate failure. Backoff: 100ms, 200ms, 400ms. On the third failure, propagate the error to Claude — don't retry forever.",
            },
          },
          {
            url: "https://github.com/langchain4j/langchain4j",
            text: "LangChain4j",
            post: ": explore MCP integration in langchain4j-anthropic",
            challenge: {
              teaser:
                "Expose your MCP server through a LangChain4j @Tool interface.",
              spec: "Create a @Tool-annotated Java method in an AiServices interface that delegates to your MCP server. Call it from Claude and assert the response uses MCP-provided data, not Claude's training knowledge (e.g., query a file you just created).",
              hint: "LangChain4j's @Tool methods are detected via reflection — the method name and @Tool description become the tool name and description Claude sees. Return a String from the method to keep things simple.",
            },
          },
        ],
        milestone:
          "Can configure and call a custom MCP server from Claude in a Java context",
      },
    ],
  },
  {
    id: 3,
    title: "Agentic Systems",
    subtitle: "Weeks 9–14",
    duration: "~75 hrs",
    color: "#B8C0D0",
    accent: "#3A5A8A",
    icon: "◈",
    goal: "Design multi-agent architectures — the highest-weighted exam domain at 27%",
    domains: ["Agentic Architecture 27%", "Context Management 15%"],
    weeks: [
      {
        week: "Weeks 9–10",
        focus: "Agentic Loops & Coordinator Patterns",
        theory: [
          {
            url: "https://anthropic.skilljar.com/introduction-to-subagents",
            text: "Skilljar: Introduction to Subagents",
          },
          {
            url: "https://docs.anthropic.com/en/docs/claude-code/sub-agents",
            text: "Read: Claude Agent SDK → Overview, Subagents, Sessions",
          },
          "Study: Hub-and-spoke coordinator–subagent topology",
          "Study: Task tool, isolated subagent contexts, fork_session/–resume",
        ],
        hands_on: [
          {
            text: "Build a coordinator agent in Java that delegates to 2 subagents",
            challenge: {
              teaser:
                "Wire a hub-and-spoke system where one coordinator owns two isolated workers.",
              spec: "ResearchCoordinator receives a topic, delegates to WebSearchAgent and SummarizerAgent (each with their own message history). Coordinator merges both outputs into a 3-paragraph brief. Assert the brief contains content tokens from both agents (not just one).",
              hint: "Pass each subagent only what it needs — WebSearch gets the raw topic, Summarizer gets the search results. Never pass one subagent's history to the other. The coordinator's context holds only the final outputs.",
            },
          },
          {
            text: "Implement the agentic loop driven by stop_reason = tool_use",
            challenge: {
              teaser:
                "Write a reusable loop that keeps calling Claude until it says it's done.",
              spec: "AgentLoop.run(prompt, tools) continues until stop_reason == end_turn, capped at 10 iterations. Throw AgentLoopException if the cap is hit. Test with a 3-step task (look up X, compute Y, format Z) — assert the loop terminates naturally before the cap.",
              hint: "Maintain a mutable List<MessageParam> that you append to after each tool call. Pass the full list on every subsequent Claude call. The loop exits only when stop_reason != 'tool_use'.",
            },
          },
          {
            url: "https://github.com/anthropics/claude-cookbooks/tree/main/patterns/agents",
            text: "Cookbook",
            post: ": agents/research_assistant notebook (translate to Java)",
            challenge: {
              teaser: "Port the research assistant to Java, end to end.",
              spec: "Your Java port must support ≥ 3 tool types, persist intermediate results between calls, and produce a markdown report. Run it on a real topic ('Latest Java 21 virtual thread improvements') and verify the report cites ≥ 2 distinct sources.",
              hint: "Use a LinkedHashMap to persist tool results by tool_use_id — reference them when building the final synthesis prompt. Don't re-fetch data Claude already retrieved.",
            },
          },
          {
            text: "Test: what breaks when context grows — observe the lost-in-the-middle effect",
            challenge: {
              teaser:
                "Find the context size where Claude starts forgetting early facts.",
              spec: "Build a 20-turn conversation that buries 'the magic word is FLAMINGO' in turn 3, then asks for recall at turn 20. Run with 8k, 32k, and 100k context. Document at which size recall first degrades (you should see it around 32k).",
              hint: "Pad each turn with ~400 tokens of filler text to reach the target context size. The recall failure is subtle — Claude may say 'I don't see a magic word' rather than hallucinating one.",
            },
          },
        ],
        milestone:
          "Can design and code a working coordinator–subagent system in Java",
      },
      {
        week: "Weeks 11–12",
        focus: "Context Management & Reliability",
        theory: [
          {
            url: "https://docs.anthropic.com/en/docs/build-with-claude/prompt-caching",
            text: "Read: Prompt Caching docs",
            post: " — cache breakpoint placement strategy",
          },
          {
            url: "https://docs.anthropic.com/en/docs/claude-code/memory",
            text: "Read: Context editing, compaction, /compact",
          },
          "Read: 1M-token window (Sonnet 4) — beta header requirement",
          "Study: Scratchpad files, persistent fact blocks, context editing patterns",
        ],
        hands_on: [
          {
            text: "Implement prompt caching in Java SDK — measure latency + cost savings",
            challenge: {
              teaser:
                "Prove caching cuts cost and latency with real before/after numbers.",
              spec: "Send a 2000-token system prompt 5× without caching, then 5× with cache_control ephemeral on the system block. Log cache_read_input_tokens vs cache_creation_input_tokens per call. Expect ≥ 80% cost reduction and ≥ 40% latency drop on cache-hit calls.",
              hint: "On the first cached call you'll see cache_creation_input_tokens > 0 and cache_read = 0. From call 2 onward you should see cache_read_input_tokens == system_prompt_token_count. If not, your cache breakpoint is placed incorrectly.",
            },
          },
          {
            text: "Build a long-running agent that manages context across 10+ turns",
            challenge: {
              teaser:
                "Prevent runaway token growth in a long multi-turn conversation.",
              spec: "SupportAgent handles a 15-turn conversation. After turn 8, auto-compact turns 1–6 into a single 'context summary' system block. Assert Claude can still answer a question about turn 2 after compaction (retrieve a fact you planted there).",
              hint: "Summarize with a separate Claude call: 'Summarize these messages in 200 words, preserving all specific facts.' Inject the summary as a system message before turn 9. Keep turns 7–8 in full.",
            },
          },
          {
            text: "Practice: cache breakpoint placement for system prompt vs dynamic content",
            challenge: {
              teaser:
                "Place the cache marker on static content only, so dynamic changes don't bust the cache.",
              spec: "3000-token static system prompt + 500-token dynamic user context per request. Place cache_control only on the static block. Verify: changing the dynamic content does not invalidate the cache — call 2 must show cache_read_input_tokens > 0.",
              hint: "The cache key is computed on everything up to and including the cache_control block. If your dynamic content comes before the breakpoint, every request is a cache miss. Move dynamic content after the breakpoint.",
            },
          },
          {
            url: "https://spring.io/projects/spring-ai",
            text: "Spring AI 1.1",
            post: ": use AnthropicChatOptions with cache settings",
            challenge: {
              teaser:
                "Configure caching in a Spring Boot service and verify it via response metadata.",
              spec: "Create a Spring @Service using AnthropicChatOptions with prompt caching enabled. Write an integration test: call the service twice with the same system prompt. Assert the second response has cache_read_input_tokens > 0 in ChatResponse metadata.",
              hint: "Access usage via response.getMetadata().getUsage(). Spring AI maps Anthropic's cache fields to AnthropicUsage — cast the Usage object to inspect cache_read_input_tokens.",
            },
          },
        ],
        milestone:
          "Can architect a production agent that manages context cost and reliability",
      },
      {
        week: "Weeks 13–14",
        focus: "Multi-Agent Research System (Exam Scenario)",
        theory: [
          {
            url: "https://anthropic.skilljar.com/introduction-to-agent-skills",
            text: "Skilljar: Introduction to Agent Skills",
          },
          {
            url: "https://docs.anthropic.com/en/docs/claude-code/hooks",
            text: "Read: Agent hooks",
            post: " — deterministic enforcement vs probabilistic prompts",
          },
          "Study: Exam scenario deep-dive: multi-agent research system",
          "Study: Context passing between agents, result aggregation patterns",
        ],
        hands_on: [
          {
            text: "Build the full multi-agent research scenario end-to-end in Java",
            challenge: {
              teaser:
                "Build the canonical 3-agent research pipeline the exam tests.",
              spec: "PlannerAgent breaks a question into 3 sub-questions. SearchAgent fetches 2 sources per sub-question. SynthesisAgent writes a structured report with citations. All agents use isolated context windows. Input: 'RAG vs fine-tuning trade-offs?' — output must have ≥ 2 citations.",
              hint: "Pass only the minimal needed context to each agent: Planner gets the question, Search gets one sub-question at a time, Synthesis gets the aggregated search results only. Never share raw histories between agents.",
            },
          },
          {
            text: "Implement hooks for pre/post-tool deterministic checks",
            challenge: {
              teaser:
                "Add hard guardrails that fire before and after every tool call.",
              spec: "PreToolHook blocks calls with a path outside /allowed/. PostToolHook redacts PAN-like patterns (16-digit sequences) from tool results before Claude sees them. Tests: (1) blocked call never reaches the tool; (2) a result with a credit card number is redacted.",
              hint: "For the PAN redact: use Pattern.compile('\\\\b\\\\d{16}\\\\b') on the result string and replace with '[REDACTED]'. Run the PostToolHook on every tool result type, including isError ones.",
            },
          },
          {
            url: "https://github.com/langchain4j/langchain4j",
            text: "LangChain4j",
            post: ": build a RAG pipeline feeding into the agent",
            challenge: {
              teaser:
                "Ground your agent in documents, not Claude's training data.",
              spec: "Ingest 5 markdown docs into an in-memory embedding store. Build retrieve_context(query) that returns top-3 chunks by cosine similarity. Wire into your research agent and verify the final report quotes text only present in your documents.",
              hint: "Use LangChain4j's EmbeddingStoreRetriever with InMemoryEmbeddingStore. Include a 'use only information from the retrieved context' instruction in your system prompt to prevent Claude from hallucinating from training data.",
            },
          },
          {
            url: "https://github.com/anthropics/claude-cookbooks/tree/main/patterns/agents",
            text: "Cookbook",
            post: ": multi_agent_research notebook — full translation to Java",
            challenge: {
              teaser: "Full Java translation with a factual correctness gate.",
              spec: "Port multi_agent_research to Java. Your port must pass: given 'Name the CEO of Anthropic', the answer must come from your RAG store, not Claude's parametric memory. Inject a doc with the correct name and a doc with a deliberate wrong name — confirm Claude picks the right one.",
              hint: "Add 'Today is [date]. Use only the provided context' to your system prompt. If Claude ignores your docs and answers from training data, your retrieval chunk isn't being injected into the prompt correctly.",
            },
          },
        ],
        milestone:
          "Have a working multi-agent research system to demo and reference in the exam",
      },
    ],
  },
  {
    id: 4,
    title: "Claude Code & CI/CD",
    subtitle: "Weeks 15–18",
    duration: "~55 hrs",
    color: "#D0C0B8",
    accent: "#8A4A3A",
    icon: "⬢",
    goal: "Master Claude Code internals — 20% of the exam and heavily scenario-tested",
    domains: ["Claude Code Configuration 20%"],
    weeks: [
      {
        week: "Weeks 15–16",
        focus: "Claude Code Configuration",
        theory: [
          {
            url: "https://anthropic.skilljar.com/claude-code-in-action",
            text: "Skilljar: Claude Code 101 + Claude Code in Action",
          },
          {
            url: "https://docs.anthropic.com/en/docs/claude-code/memory",
            text: "Read: code.claude.com → Memory/CLAUDE.md hierarchy with @path imports",
          },
          {
            url: "https://docs.anthropic.com/en/docs/claude-code/settings",
            text: "Read: .claude/rules/ with YAML frontmatter, .claude/skills/",
          },
          "Study: context: fork / allowed-tools / argument-hint in skills",
        ],
        hands_on: [
          {
            text: "Set up Claude Code locally, create a CLAUDE.md for a real Java project",
            challenge: {
              teaser:
                "Write a CLAUDE.md that actually changes Claude's behavior on your codebase.",
              spec: "CLAUDE.md must include: preferred test framework (JUnit 5), import order rules, banned pattern (no System.out.println), and a /review slash command. Ask Claude Code to review a real file and verify it flags all 4 rules.",
              hint: "Start each rule with 'Always' or 'Never' — vague instructions get ignored. For the slash command, define it as a CLAUDE.md heading: ## /review → 'Check this file for the rules listed above and output a summary.'",
            },
          },
          {
            text: "Build a multi-level CLAUDE.md hierarchy (root + module-level)",
            challenge: {
              teaser:
                "Prove that module-level CLAUDE.md overrides root rules within its directory.",
              spec: "Root CLAUDE.md: global Java style. /payments/CLAUDE.md: require a PCI-DSS comment on any method touching card data. Open a /payments/ file, ask Claude to add a method — assert PCI comment present. Open a non-payments file — assert absent.",
              hint: "Claude Code merges CLAUDE.md files from root to the current file's directory — more specific files win. Test with Claude Code open in the /payments/ directory vs the root to observe the scoping difference.",
            },
          },
          {
            text: "Create 2 custom skills with fork context",
            challenge: {
              teaser:
                "Build two skills with isolated contexts and verify the fork boundary.",
              spec: "skill-1: sql-review — warn on DELETE/UPDATE without WHERE. skill-2: javadoc — generate /** */ for undocumented public methods. Both use context: fork. Verify sql-review flags a missing WHERE clause and javadoc produces a complete block for a bare method.",
              hint: "context: fork means the skill runs in a separate context window from the main conversation — it can't read your conversation history. This is good for focused tasks. Set allowed-tools: [] to prevent the skill from taking unintended actions.",
            },
          },
          {
            text: "Use plan mode on a complex refactoring task — observe how it reasons",
            challenge: {
              teaser: "Critique Claude's refactoring plan before approving it.",
              spec: "Ask Claude Code in plan mode to split a 500+ line God class into 3 focused classes. Do not approve. Write a 1-page analysis: does the plan correctly identify all responsibilities? Does the proposed split follow SRP? Note where the plan is right and wrong.",
              hint: "A good plan should name the 3 new classes and what each owns. Common failure: Claude proposes a split by file type (DTOs, services, repos) rather than by business domain. Check if the split would actually reduce coupling.",
            },
          },
        ],
        milestone:
          "Can configure CLAUDE.md + skills for a production Java project",
      },
      {
        week: "Weeks 17–18",
        focus: "Headless Mode, CI/CD & Structured Output",
        theory: [
          {
            url: "https://code.claude.com/docs/en/headless",
            text: "Read: Claude Code → -p headless mode, –output-format json –json-schema",
          },
          {
            url: "https://docs.anthropic.com/en/docs/claude-code/github-actions",
            text: "Read: GitHub Actions integration, GitLab CI/CD docs",
          },
          "Study: Exam scenario — Claude Code in CI/CD pipeline",
          "Study: Exam scenario — developer productivity tools",
        ],
        hands_on: [
          {
            text: "Wire Claude Code into a GitHub Actions workflow (code review job)",
            challenge: {
              teaser:
                "Get Claude to post a real code review comment on a pull request.",
              spec: "Create .github/workflows/claude-review.yml — triggers on pull_request, runs claude -p headless --output-format json, posts the review via GitHub API as a PR comment. Test with a PR containing a deliberate null pointer bug — confirm the comment identifies it.",
              hint: "Use the GITHUB_TOKEN secret to authenticate the gh api PR comment call. Pipe claude's stdout through jq '.result' to extract the review text before posting. The workflow must not fail if Claude finds no issues.",
            },
          },
          {
            text: "Use -p headless mode to extract structured JSON from Claude Code output",
            challenge: {
              teaser:
                "Parse structured JSON out of Claude Code's stdout in a shell script.",
              spec: "Run: claude -p 'Analyze for NPE risks. Respond ONLY as JSON: {risks:[{line,description,severity}]}' --output-format json on a file with 2 known NPE risks. Shell script must assert risks.length >= 2 and each entry has all 3 fields.",
              hint: "Use jq '.result | fromjson | .risks | length' to count risks and jq '.result | fromjson | .risks[] | {line,description,severity}' to validate fields. Add 'no markdown, no prose, only JSON' to your prompt.",
            },
          },
          {
            text: "Build a developer-productivity tool: automated PR description generator",
            challenge: {
              teaser:
                "Auto-generate a useful PR description from git diff + commit log.",
              spec: "Shell script: pipe git diff --stat and git log --oneline into claude -p headless. Output must have 3 sections: What Changed, Why, and Testing. Assert all 3 sections are present and non-empty for a real branch with ≥ 3 commits.",
              hint: "Use a heredoc to inject both the diff stat and the commit log into one prompt. Tell Claude 'Output exactly 3 markdown sections with these exact headings: ## What Changed, ## Why, ## Testing.'",
            },
          },
          {
            url: "https://anthropic.skilljar.com/claude-code-in-action",
            text: "Skilljar: Claude Code in Action",
            post: " (complete full course)",
            challenge: {
              teaser:
                "Apply one new technique from the course to a real project and measure it.",
              spec: "After completing the course, pick one technique you hadn't used before. Document: the technique, your before/after prompt or config change, and a measurable outcome (reduced turns, higher accuracy, or faster execution). Before/after must differ by ≥ 1 measurable metric.",
              hint: "Good candidates: multi-file context management, custom slash commands, or the Explore→Plan→Code→Commit workflow. Measure turns-to-completion on the same task before and after applying the technique.",
            },
          },
        ],
        milestone:
          "Have a working CI/CD integration using Claude Code headless mode",
      },
    ],
  },
  {
    id: 5,
    title: "Production & Evals",
    subtitle: "Weeks 19–22",
    duration: "~50 hrs",
    color: "#C8C0D8",
    accent: "#5A3A8A",
    icon: "◇",
    goal: "Build production-grade systems with cost control, evals, and enterprise patterns",
    domains: ["Agentic Architecture 27%", "Prompt Engineering 20%"],
    weeks: [
      {
        week: "Weeks 19–20",
        focus: "Evaluations & Quality",
        theory: [
          {
            url: "https://docs.anthropic.com/en/docs/test-and-evaluate/eval-tool",
            text: "Read: Anthropic Docs → Test and Evaluate suite, Workbench evals",
          },
          {
            url: "https://www.promptfoo.dev/docs/providers/anthropic/",
            text: "Read: promptfoo integration",
            post: ", LLM-as-judge patterns",
          },
          "Study: Stratified sampling, field-level confidence calibration",
          "Study: Hallucination mitigation, consistency, prompt-leak reduction",
        ],
        hands_on: [
          {
            url: "https://www.promptfoo.dev/",
            text: "Set up promptfoo",
            post: " against your multi-agent research system",
            challenge: {
              teaser:
                "Build a 20-case eval suite that covers happy path, edge cases, and adversarial inputs.",
              spec: "20 test cases: 5 factual questions with ground-truth, 5 edge cases (empty/very long/non-English input), 5 adversarial prompts, 5 regressions from past failures. Full suite must run in < 60s. Assert ≥ 80% pass rate.",
              hint: "Use promptfoo's defaultTest to set shared provider/model config. For factual assertions, use contains: or llm-rubric: with a specific rubric. For adversarial tests, assert notContains: harmful keywords.",
            },
          },
          {
            text: "Build a basic LLM-as-judge eval in Java (Claude grades Claude outputs)",
            challenge: {
              teaser:
                "Use Claude to score Claude — then calibrate the judge against your own ratings.",
              spec: "JudgeEvaluator.score(question, answer) calls Claude with a rubric: correctness 0–3, completeness 0–3, conciseness 0–2. Run on 10 answer pairs. Assert judge scores correlate with your manual ratings (Pearson r > 0.7).",
              hint: "Include the rubric as explicit numbered criteria in the judge prompt: '1. Correctness (0-3): Is every factual claim correct?' Ask Claude to return JSON: {correctness: N, completeness: N, conciseness: N, rationale: '...'}.",
            },
          },
          {
            url: "https://console.anthropic.com/workbench",
            text: "Anthropic Workbench",
            post: ": run evals on your prompt variations",
            challenge: {
              teaser:
                "A/B test 3 system-prompt variants and pick a winner with data.",
              spec: "Create 3 variants (terse, verbose, chain-of-thought). Run the same 10 prompts against each in Workbench. Export results and identify the winner on your primary metric. Write a 1-paragraph justification with specific numbers.",
              hint: "Export to CSV and compute average score per variant. Look for variance within a variant too — high variance means the prompt is fragile even if average score is good.",
            },
          },
          {
            text: "Create a regression test suite for your 3 built projects",
            challenge: {
              teaser: "Catch regressions before they reach the exam reviewer.",
              spec: "RegressionSuite: 5 golden-path scenarios across your tool-calling app, multi-agent system, and CI/CD integration. Each test asserts on specific response content (not just HTTP 200). All 5 must pass before advancing to Phase 6.",
              hint: "For each scenario, identify the one thing that must be true: a specific tool was called, a specific JSON key is present, a specific phrase appears. Narrow assertions are more maintainable than broad ones.",
            },
          },
        ],
        milestone:
          "Can design and run a meaningful eval suite for a Claude-powered system",
      },
      {
        week: "Weeks 21–22",
        focus: "Cost, Latency & Batch API",
        theory: [
          {
            url: "https://docs.anthropic.com/en/docs/build-with-claude/message-batches",
            text: "Read: Batch API docs",
            post: " — 50% discount, 24-hour processing ceiling",
          },
          {
            url: "https://docs.anthropic.com/en/docs/about-claude/models/overview",
            text: "Read: Model right-sizing strategy",
            post: ": Opus 4.7 vs Sonnet 4.6 vs Haiku 4.5",
          },
          {
            url: "https://docs.anthropic.com/en/docs/build-with-claude/extended-thinking",
            text: "Study: Extended thinking",
            post: " — when to use, token budget sizing",
          },
          {
            url: "https://docs.anthropic.com/en/docs/build-with-claude/prompt-caching",
            text: "Study: Output-token caps, prompt caching ROI calculations",
          },
        ],
        hands_on: [
          {
            text: "Implement Batch API in Java for async bulk processing job",
            challenge: {
              teaser: "Submit 50 prompts in one batch and poll to completion.",
              spec: "Submit a batch of 50 classification prompts. Poll every 30s. Parse all results and compute the distribution across 5 categories. Assert ≥ 90% success rate and that total cost is < 55% of what 50 synchronous calls would cost.",
              hint: "Use the batches.create() endpoint with a list of BatchRequest objects. Each needs a custom_id for correlation. On poll, check processing_status == 'ended' before fetching results via batches.results().",
            },
          },
          {
            text: "Build a cost-calculator: estimate spend across 3 model tiers for a given workload",
            challenge: {
              teaser:
                "Model the cost difference between Haiku, Sonnet, and Opus for a real workload.",
              spec: "CostEstimator.estimate(model, inputTokens, outputTokens, callsPerDay) → {daily, monthly} USD. At 10k calls/day × 500in/200out: assert Haiku cost ≤ 10% of Opus cost. Use current published pricing.",
              hint: "Hardcode pricing as constants (they change rarely): Haiku is ~100× cheaper than Opus per token. Pull the latest prices from docs.anthropic.com/pricing before coding — don't rely on training data values.",
            },
          },
          {
            text: "Implement extended thinking on a complex reasoning task — compare outputs",
            challenge: {
              teaser:
                "Measure whether a larger thinking budget produces a meaningfully better design.",
              spec: "Ask 'Design a DB schema for a ride-sharing app' with standard vs extended thinking (budget_tokens: 8000). Score both on a 10-point rubric (normalization, edge cases, indexing). Extended thinking should win by ≥ 2 points.",
              hint: "In extended thinking mode you'll see a thinking content block before the answer block. The thinking block is Claude's scratchpad — read it to understand why the answer differs. Don't include the thinking block in your rubric score.",
            },
          },
          {
            text: "Profile your multi-agent system: identify top 3 cost/latency hotspots",
            challenge: {
              teaser:
                "Find where your Phase 3 system spends the most time and money.",
              spec: "Instrument your multi-agent system with per-call timing and token logging. Run 10 end-to-end requests. Produce a table: agent name, avg_latency_ms, avg_input_tokens, avg_output_tokens, estimated_cost. Propose and measure one optimization (e.g., downsize a subagent to Haiku).",
              hint: "Wrap each API call in a try-finally that logs System.nanoTime() delta and response.usage(). The SynthesisAgent typically dominates output tokens — that's often where switching to a cheaper model saves the most.",
            },
          },
        ],
        milestone:
          "Can architect a production system with explicit cost and latency controls",
      },
    ],
  },
  {
    id: 6,
    title: "Exam Sprint",
    subtitle: "Weeks 23–26",
    duration: "~40 hrs",
    color: "#C8D0B8",
    accent: "#4A7A3A",
    icon: "★",
    goal: "Solidify all domains, attack weak spots, and pass CCA-F with confidence",
    domains: ["All 5 Domains — Full Review"],
    weeks: [
      {
        week: "Weeks 23–24",
        focus: "Full Domain Review + Practice Tests",
        theory: [
          {
            url: "https://github.com/paullarionov/claude-certified-architect",
            text: "Larionov GitHub guide",
            post: ": read end-to-end (paullarionov/claude-certified-architect)",
          },
          {
            url: "https://claudecertifications.com/claude-certified-architect",
            text: "claudecertifications.com",
            post: ": complete free 25-question practice set",
          },
          {
            url: "https://tutorialsdojo.com/cca-f-claude-certified-architect-foundations-study-guide/",
            text: "Tutorials Dojo",
            post: ": CCA-F study guide + practice exam",
          },
          {
            url: "https://medium.com/@richardhightower/claude-certified-architect-practice-exam-60-questions-with-detailed-explanations-3a4d2267603d",
            text: "Rick Hightower (Towards AI)",
            post: ": 60-question practice exam with explanations",
          },
        ],
        hands_on: [
          {
            url: "https://anthropic.skilljar.com/claude-certified-architect-foundations-access-request",
            text: "Take the official Anthropic practice test",
            post: " (provided after registration)",
            challenge: {
              teaser:
                "Use your test results as a diagnostic, not just a score.",
              spec: "After the test, categorize every wrong answer by domain. If ≥ 3 errors fall in one domain, build a focused 1-day mini-project for that domain before Week 25. Write a 1-sentence root-cause for each wrong answer.",
              hint: "Wrong answers usually cluster around 2 failure modes: terminology (confusing tool_use with function calling) or decision heuristics (when to use caching vs compaction). Root-cause analysis reveals which one is your issue.",
            },
          },
          {
            text: "Score by domain — identify your 2 weakest domains",
            challenge: {
              teaser:
                "Turn your scores into an actionable weak-domain hit list.",
              spec: "Spreadsheet: domain × practice-test, with score %. Plot a trend line per domain. Identify the 2 lowest averages. Write a specific, measurable study goal for each (e.g., 'Build one agent that uses all 4 stop_reason values').",
              hint: "Use Google Sheets SPARKLINE() to visualize trend per domain in one cell. Domains below 65% need a new project — reading alone won't fix pattern-recognition gaps.",
            },
          },
          {
            text: "Build a mini-project targeting each weak domain",
            challenge: {
              teaser:
                "Build the smallest possible app that forces mastery of the weak domain.",
              spec: "Per weak domain, build a minimal Java app that exercises the core exam scenario. Pass criterion: your own JUnit test suite passes. E.g., Context Mgmt → agent that survives 20 turns with flat cost via caching + compaction.",
              hint: "Keep it under 200 lines. The goal is muscle memory for the pattern, not a portfolio piece. Time-box each mini-project to 4 hours — if you're still stuck, the problem is concept, not implementation.",
            },
          },
          {
            text: "Review all 6 exam scenarios and confirm you can describe an architecture for each",
            challenge: {
              teaser: "Recall every scenario from memory in under 10 minutes.",
              spec: "For each of the 6 scenarios, write from memory: key components, critical design decision, most common anti-pattern. Time yourself — target under 10 min total. Anything you can't recall without notes is a gap to close this week.",
              hint: "The 6 scenarios are: Multi-Agent Research, Code Generation, Developer Productivity Tools, Claude Code in CI/CD, Structured Data Extraction, and Customer Support Bot. If you blanked on any, that's your mini-project for the week.",
            },
          },
        ],
        milestone:
          "Scoring 75%+ on practice tests consistently across all domains",
      },
      {
        week: "Weeks 25–26",
        focus: "Final Polish & Exam",
        theory: [
          {
            url: "https://anthropic.skilljar.com/claude-certified-architect-foundations-access-request",
            text: "Re-read the official Exam Guide PDF",
            post: " — all 12 sample scenario questions",
          },
          "Flashcards: MCP config syntax, CLAUDE.md hierarchy rules, API header requirements",
          {
            url: "https://github.com/paullarionov/claude-certified-architect/blob/main/guide_en.MD",
            text: "Review: anti-patterns section of Larionov guide",
          },
          {
            url: "https://anthropic.skilljar.com/",
            text: "Light review of Skilljar courses you found weakest",
          },
        ],
        hands_on: [
          {
            text: "Final run-through of your 3 built systems — can you explain every design decision?",
            challenge: {
              teaser:
                "Write a design doc for each project — if you can't justify it, fix it.",
              spec: "For each project (tool-calling app, multi-agent system, CI/CD integration): 1-page design doc with problem, approach, key trade-offs, and what you'd change. If you can't justify a decision from memory, refactor before exam day.",
              hint: "Focus on trade-offs the exam loves: why prompt-based vs deterministic enforcement, why caching that specific block, why Sonnet vs Haiku for a subagent. These are the exact types of 'which is best and why' questions on the exam.",
            },
          },
          {
            text: "One final full practice exam under timed conditions (120 min / 60 questions)",
            challenge: {
              teaser:
                "Simulate exam conditions exactly — no pausing, no lookups.",
              spec: "Timer: 120 min. No references. Score immediately. Aim ≥ 80%. For every wrong or guessed answer, write the correct explanation in your own words before checking the key. This is the most important single study action of the whole 26 weeks.",
              hint: "Flag uncertain questions and return to them — don't spend > 2 min on any one. 60 questions / 120 min = 2 min/question. Scenario questions often have 2 plausible answers; the right one is the one that is more defensive, more explicit, or more production-grade.",
            },
          },
          {
            url: "https://anthropic.skilljar.com/claude-certified-architect-foundations-access-request",
            text: "Register and sit CCA-F exam on Skilljar",
            challenge: {
              teaser: "A 5-minute warm-up ritual before clicking Start.",
              spec: "Before starting the exam, spend 5 minutes writing the 5 domain names and their weights from memory. If you can recall all 5 without error, proceed. This retrieval exercise primes the relevant knowledge nodes right before you need them.",
              hint: "Domains: Agentic Architecture 27%, Claude Code Config 20%, Prompt Engineering 20%, Tool Design & MCP 18%, Context Management 15%. If you miss any, spend 10 more minutes on flash cards before starting.",
            },
          },
          {
            text: "🎯 Target: pass with 800+ (well above the 720 threshold)",
            challenge: {
              teaser:
                "Write a post-exam retrospective whether you pass or not.",
              spec: "After the exam: document which question types surprised you, which of your 3 projects helped most, and what you'd study differently. This closes the learning loop and becomes your reference for the next certification.",
              hint: "The most useful retros are specific: 'I missed 4/5 prompt-caching questions because I memorized the concept but never implemented cache breakpoint placement under time pressure.' Vague retros don't improve future performance.",
            },
          },
        ],
        milestone: "CCA-F certified ✓",
      },
    ],
  },
];

const examDomains = [
  { name: "Agentic Architecture", weight: 27, color: "#3A5A8A" },
  { name: "Claude Code Config", weight: 20, color: "#8A4A3A" },
  { name: "Prompt Engineering", weight: 20, color: "#8B6F47" },
  { name: "Tool Design & MCP", weight: 18, color: "#4A7A4A" },
  { name: "Context Management", weight: 15, color: "#5A3A8A" },
];
const javaStack = [
  {
    name: "anthropic-java SDK",
    version: "2.26.0",
    use: "Primary API client — sync/async, streaming, tool use, batch",
  },
  {
    name: "LangChain4j",
    version: "1.13.1",
    use: "Agentic systems, RAG pipelines, multi-provider",
  },
  {
    name: "Spring AI",
    version: "1.1.3",
    use: "Spring Boot integration, Micrometer observability",
  },
  {
    name: "MCP Java SDK",
    version: "0.15.0",
    use: "Build and consume MCP servers",
  },
];

function StudyPlan() {
  const [activePhase, setActivePhase] = useState(null);
  const [activeWeek, setActiveWeek] = useState(null);
  return (
    <div
      style={{
        fontFamily: "'Georgia', 'Times New Roman', serif",
        background: "#F5F0E8",
        height: "100%",
        color: "#2C2416",
        overflowY: "auto",
      }}
    >
      <div
        style={{
          background: "#2C2416",
          color: "#F5F0E8",
          padding: "40px 40px 36px",
          borderBottom: "4px solid #8B6F47",
        }}
      >
        <div style={{ maxWidth: 820, margin: "0 auto" }}>
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.25em",
              color: "#8B6F47",
              marginBottom: 12,
              textTransform: "uppercase",
            }}
          >
            Claude Certified Architect — Foundations
          </div>
          <h1
            style={{
              fontSize: 34,
              fontWeight: "normal",
              margin: "0 0 8px",
              lineHeight: 1.1,
              letterSpacing: "-0.5px",
            }}
          >
            6-Month Study Plan
          </h1>
          <p
            style={{
              fontSize: 14,
              color: "#B8A898",
              margin: "0 0 24px",
              fontStyle: "italic",
            }}
          >
            Java Developer Track · 10–15 hrs/week · ~325 hrs total
          </p>
          <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
            {[
              ["60 Questions", "120 minutes"],
              ["$99 USD", "via Skilljar"],
              ["720 / 1000", "passing score"],
              ["6 Scenarios", "4 drawn per exam"],
            ].map(([val, sub]) => (
              <div key={val}>
                <div
                  style={{ fontSize: 18, fontWeight: "bold", color: "#C8B8A2" }}
                >
                  {val}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#8B7A6A",
                    letterSpacing: "0.1em",
                  }}
                >
                  {sub}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{ maxWidth: 820, margin: "0 auto", padding: "36px 40px" }}>
        {/* Domain weights */}
        <div style={{ marginBottom: 40 }}>
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "#8B6F47",
              marginBottom: 14,
            }}
          >
            Exam Domain Weights
          </div>
          <div
            style={{
              display: "flex",
              height: 30,
              borderRadius: 4,
              overflow: "hidden",
              marginBottom: 10,
            }}
          >
            {examDomains.map((d) => (
              <div
                key={d.name}
                style={{
                  width: `${d.weight}%`,
                  background: d.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  color: "white",
                  fontWeight: "bold",
                }}
              >
                {d.weight}%
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {examDomains.map((d) => (
              <div
                key={d.name}
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    background: d.color,
                  }}
                />
                <span style={{ fontSize: 12, color: "#5A4A3A" }}>{d.name}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Java stack */}
        <div
          style={{
            marginBottom: 40,
            background: "#EDE8DE",
            border: "1px solid #D4C8B4",
            borderRadius: 6,
            padding: 22,
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "#8B6F47",
              marginBottom: 14,
            }}
          >
            Your Java Stack
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 10,
            }}
          >
            {javaStack.map((s) => (
              <div
                key={s.name}
                style={{
                  background: "#F5F0E8",
                  padding: "12px 14px",
                  borderRadius: 4,
                  borderLeft: "3px solid #8B6F47",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontWeight: "bold", fontSize: 13 }}>
                    {s.name}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      color: "#8B6F47",
                      fontFamily: "monospace",
                      background: "#EDE8DE",
                      padding: "2px 6px",
                      borderRadius: 3,
                    }}
                  >
                    v{s.version}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "#6A5A4A" }}>{s.use}</div>
              </div>
            ))}
          </div>
        </div>
        {/* Phases */}
        <div
          style={{
            fontSize: 11,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: "#8B6F47",
            marginBottom: 16,
          }}
        >
          Study Phases — click to expand
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {phases.map((phase) => {
            const isOpen = activePhase === phase.id;
            return (
              <div
                key={phase.id}
                style={{
                  border: `1px solid ${isOpen ? phase.accent : "#D4C8B4"}`,
                  borderRadius: 6,
                  overflow: "hidden",
                }}
              >
                <button
                  onClick={() => setActivePhase(isOpen ? null : phase.id)}
                  style={{
                    width: "100%",
                    background: isOpen ? phase.accent : phase.color,
                    border: "none",
                    padding: "18px 22px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    textAlign: "left",
                  }}
                >
                  <span
                    style={{
                      fontSize: 20,
                      color: isOpen ? "#F5F0E8" : phase.accent,
                    }}
                  >
                    {phase.icon}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 10,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 17,
                          fontWeight: "bold",
                          color: isOpen ? "#F5F0E8" : "#2C2416",
                        }}
                      >
                        Phase {phase.id}: {phase.title}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          color: isOpen ? "#D4C8B4" : "#8B7A6A",
                        }}
                      >
                        {phase.subtitle}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          color: isOpen ? "#D4C8B4" : "#8B7A6A",
                          marginLeft: "auto",
                        }}
                      >
                        {phase.duration}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: isOpen ? "#E8DDD0" : "#5A4A3A",
                        marginTop: 3,
                        fontStyle: "italic",
                      }}
                    >
                      {phase.goal}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 16,
                      color: isOpen ? "#F5F0E8" : phase.accent,
                    }}
                  >
                    {isOpen ? "▲" : "▼"}
                  </span>
                </button>
                {isOpen && (
                  <div style={{ background: "#F9F6F0", padding: 22 }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        marginBottom: 18,
                        flexWrap: "wrap",
                      }}
                    >
                      {phase.domains.map((d) => (
                        <span
                          key={d}
                          style={{
                            fontSize: 11,
                            background: phase.color,
                            color: phase.accent,
                            padding: "3px 10px",
                            borderRadius: 20,
                            fontWeight: "bold",
                            letterSpacing: "0.05em",
                          }}
                        >
                          {d}
                        </span>
                      ))}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                      }}
                    >
                      {phase.weeks.map((w) => {
                        const wk = `${phase.id}-${w.week}`;
                        const wOpen = activeWeek === wk;
                        return (
                          <div
                            key={w.week}
                            style={{
                              border: "1px solid #D4C8B4",
                              borderRadius: 4,
                              overflow: "hidden",
                            }}
                          >
                            <button
                              onClick={() => setActiveWeek(wOpen ? null : wk)}
                              style={{
                                width: "100%",
                                background: wOpen ? "#EDE8DE" : "#F5F0E8",
                                border: "none",
                                padding: "12px 16px",
                                cursor: "pointer",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                textAlign: "left",
                              }}
                            >
                              <div>
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: phase.accent,
                                    fontWeight: "bold",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.1em",
                                  }}
                                >
                                  {w.week}
                                </span>
                                <span
                                  style={{
                                    fontSize: 13,
                                    fontWeight: "bold",
                                    color: "#2C2416",
                                    marginLeft: 10,
                                  }}
                                >
                                  {w.focus}
                                </span>
                              </div>
                              <span style={{ color: phase.accent }}>
                                {wOpen ? "−" : "+"}
                              </span>
                            </button>
                            {wOpen && (
                              <div
                                style={{
                                  padding: "14px 16px 16px",
                                  background: "#FDFBF7",
                                }}
                              >
                                {/* Row 1 — Study & Read (full width) */}
                                <div style={{ marginBottom: 18 }}>
                                  <div
                                    style={{
                                      fontSize: 11,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.15em",
                                      color: "#8B6F47",
                                      marginBottom: 8,
                                      fontWeight: "bold",
                                    }}
                                  >
                                    📚 Study & Read
                                  </div>
                                  <ul
                                    style={{ margin: 0, padding: "0 0 0 16px" }}
                                  >
                                    {w.theory.map((t, i) => (
                                      <li
                                        key={i}
                                        style={{
                                          fontSize: 12,
                                          color: "#3A2E22",
                                          marginBottom: 5,
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        <ResourceItem
                                          item={t}
                                          accent={phase.accent}
                                        />
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                {/* Row 2 — Build & Code (2-column grid inside) */}
                                <div style={{ marginBottom: 14 }}>
                                  <div
                                    style={{
                                      fontSize: 11,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.15em",
                                      color: "#4A7A4A",
                                      marginBottom: 8,
                                      fontWeight: "bold",
                                    }}
                                  >
                                    ⌨️ Build & Code
                                  </div>
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 1fr",
                                      gap: "4px 18px",
                                    }}
                                  >
                                    {w.hands_on.map((h, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          fontSize: 12,
                                          color: "#3A2E22",
                                          lineHeight: 1.5,
                                          display: "flex",
                                          gap: 6,
                                          alignItems: "flex-start",
                                          paddingBottom: 8,
                                        }}
                                      >
                                        <span
                                          style={{
                                            color: phase.accent,
                                            fontWeight: 700,
                                            flexShrink: 0,
                                            marginTop: 1,
                                          }}
                                        >
                                          ›
                                        </span>
                                        <span>
                                          <ResourceItem
                                            item={h}
                                            accent={phase.accent}
                                          />
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    background: phase.color,
                                    borderLeft: `3px solid ${phase.accent}`,
                                    padding: "9px 12px",
                                    borderRadius: "0 4px 4px 0",
                                    fontSize: 12,
                                    color: "#2C2416",
                                  }}
                                >
                                  <strong>✓ Milestone:</strong> {w.milestone}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* Resources */}
        <div
          style={{
            marginTop: 40,
            borderTop: "2px solid #D4C8B4",
            paddingTop: 28,
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "#8B6F47",
              marginBottom: 16,
            }}
          >
            Essential Resources
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 14,
            }}
          >
            {[
              {
                label: "Official",
                items: [
                  {
                    url: "https://anthropic.skilljar.com/",
                    text: "anthropic.skilljar.com",
                    post: " (13 free courses)",
                  },
                  {
                    url: "https://docs.anthropic.com/",
                    text: "platform.claude.com/docs",
                  },
                  {
                    url: "https://docs.anthropic.com/en/docs/claude-code/overview",
                    text: "code.claude.com",
                    post: " (Claude Code docs)",
                  },
                  {
                    url: "https://github.com/anthropics/claude-cookbooks",
                    text: "Anthropic Cookbook",
                    post: " (GitHub)",
                  },
                ],
              },
              {
                label: "Community",
                items: [
                  {
                    url: "https://github.com/paullarionov/claude-certified-architect",
                    text: "paullarionov/claude-certified-architect",
                    post: " (GitHub ★744)",
                  },
                  {
                    url: "https://claudecertifications.com/claude-certified-architect",
                    text: "claudecertifications.com",
                    post: " (free 12-wk plan)",
                  },
                  {
                    url: "https://tutorialsdojo.com/cca-f-claude-certified-architect-foundations-study-guide/",
                    text: "Tutorials Dojo CCA-F study guide",
                  },
                  {
                    url: "https://medium.com/@richardhightower/claude-certified-architect-practice-exam-60-questions-with-detailed-explanations-3a4d2267603d",
                    text: "Rick Hightower – 60Q practice exam",
                    post: " (Medium)",
                  },
                ],
              },
              {
                label: "Practice Tests",
                items: [
                  {
                    url: "https://anthropic.skilljar.com/claude-certified-architect-foundations-access-request",
                    text: "Official Anthropic practice test",
                    post: " (post-registration)",
                  },
                  {
                    url: "https://claudecertifications.com/claude-certified-architect",
                    text: "claudecertifications.com",
                    post: " (25 free questions)",
                  },
                  {
                    url: "https://www.udemy.com/course/claude-certified-architect-foundations-cca-f-practice-exams/",
                    text: "Udemy CCAF practice exams",
                    post: " (supplement)",
                  },
                  {
                    url: "https://console.anthropic.com/workbench",
                    text: "Workbench evals",
                    post: " (hands-on practice)",
                  },
                ],
              },
            ].map((s) => (
              <div
                key={s.label}
                style={{ background: "#EDE8DE", borderRadius: 6, padding: 16 }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: "bold",
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    color: "#8B6F47",
                    marginBottom: 10,
                  }}
                >
                  {s.label}
                </div>
                <ul style={{ margin: 0, padding: "0 0 0 14px" }}>
                  {s.items.map((item, i) => (
                    <li
                      key={i}
                      style={{
                        fontSize: 12,
                        color: "#3A2E22",
                        marginBottom: 5,
                        lineHeight: 1.5,
                      }}
                    >
                      <ResourceItem item={item} accent="#8B6F47" />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div
          style={{
            marginTop: 28,
            padding: "14px 18px",
            background: "#2C2416",
            borderRadius: 6,
            color: "#B8A898",
            fontSize: 12,
            lineHeight: 1.6,
          }}
        >
          <strong style={{ color: "#C8B8A2" }}>Important:</strong> CCA-F is
          currently gated to Claude Partner Network members. Verify your
          organization's access at anthropic.skilljar.com before scheduling.
          Broader public availability is expected later in 2026.
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// NAV WRAPPER
// ─────────────────────────────────────────────

const NAV_ITEMS = [
  {
    id: "loan",
    label: "Auto Loan Calculator",
    icon: "💰",
    tag: "Finance",
    tagColor: "#00D9A3",
    desc: "Payoff simulator with lump sum modeling",
  },
  {
    id: "study",
    label: "CCA Study Plan",
    icon: "📘",
    tag: "Learning",
    tagColor: "#8B6F47",
    desc: "6-month Java developer track",
  },
];

export default function Dashboard() {
  const [active, setActive] = useState("loan");
  const [collapsed, setCollapsed] = useState(false);

  const current = NAV_ITEMS.find((n) => n.id === active);

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        fontFamily: "'Sora', sans-serif",
        background: "#0C0C18",
        overflow: "hidden",
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Mono:wght@400;500;600&display=swap"
        rel="stylesheet"
      />

      {/* Sidebar */}
      <div
        style={{
          width: collapsed ? 60 : 220,
          minWidth: collapsed ? 60 : 220,
          background: "#0A0A14",
          borderRight: "1px solid rgba(255,255,255,0.06)",
          display: "flex",
          flexDirection: "column",
          transition: "width 0.25s ease, min-width 0.25s ease",
          overflow: "hidden",
        }}
      >
        {/* Logo row */}
        <div
          style={{
            padding: collapsed ? "20px 0" : "22px 18px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "space-between",
            borderBottom: "1px solid rgba(255,255,255,0.05)",
          }}
        >
          {!collapsed && (
            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#E0E0EC",
                  letterSpacing: "-0.02em",
                }}
              >
                Adi's
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "#383858",
                  fontFamily: "'DM Mono', monospace",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                Dashboard
              </div>
            </div>
          )}
          <button
            onClick={() => setCollapsed((c) => !c)}
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 8,
              width: 28,
              height: 28,
              cursor: "pointer",
              color: "#555",
              fontSize: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {collapsed ? "›" : "‹"}
          </button>
        </div>

        {/* Nav items */}
        <div
          style={{
            flex: 1,
            padding: collapsed ? "12px 8px" : "14px 10px",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {!collapsed && (
            <div
              style={{
                fontSize: 9,
                color: "#2A2A42",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                fontFamily: "'DM Mono', monospace",
                padding: "4px 8px 8px",
              }}
            >
              Apps
            </div>
          )}
          {NAV_ITEMS.map((item) => {
            const isActive = active === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActive(item.id)}
                title={collapsed ? item.label : undefined}
                style={{
                  width: "100%",
                  background: isActive
                    ? "rgba(255,255,255,0.06)"
                    : "transparent",
                  border: `1px solid ${isActive ? "rgba(255,255,255,0.1)" : "transparent"}`,
                  borderRadius: 10,
                  padding: collapsed ? "10px 0" : "10px 12px",
                  cursor: "pointer",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: collapsed ? 0 : 10,
                  justifyContent: collapsed ? "center" : "flex-start",
                  transition: "all 0.15s",
                }}
              >
                <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>
                  {item.icon}
                </span>
                {!collapsed && (
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? "#E0E0EC" : "#666",
                        lineHeight: 1.3,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.label}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: isActive ? item.tagColor : "#333348",
                        marginTop: 2,
                        fontFamily: "'DM Mono', monospace",
                      }}
                    >
                      {item.tag}
                    </div>
                  </div>
                )}
                {!collapsed && isActive && (
                  <div
                    style={{
                      marginLeft: "auto",
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      background: item.tagColor,
                      flexShrink: 0,
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        {!collapsed && (
          <div
            style={{
              padding: "12px 18px 16px",
              borderTop: "1px solid rgba(255,255,255,0.04)",
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: "#1E1E32",
                fontFamily: "'DM Mono', monospace",
                lineHeight: 1.5,
              }}
            >
              Built with Claude
              <br />
              claude.ai
            </div>
          </div>
        )}
      </div>

      {/* Main stage */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Top bar */}
        <div
          style={{
            height: 48,
            background: "#0A0A14",
            borderBottom: "1px solid rgba(255,255,255,0.05)",
            display: "flex",
            alignItems: "center",
            padding: "0 24px",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 16 }}>{current.icon}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#C0C0D8" }}>
            {current.label}
          </span>
          <span
            style={{
              fontSize: 11,
              color: "#252540",
              fontFamily: "'DM Mono', monospace",
            }}
          >
            ·
          </span>
          <span
            style={{
              fontSize: 11,
              color: "#333358",
              fontFamily: "'DM Mono', monospace",
            }}
          >
            {current.desc}
          </span>
          <div
            style={{
              marginLeft: "auto",
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.07)",
              borderRadius: 20,
              padding: "3px 10px",
            }}
          >
            <span
              style={{
                fontSize: 10,
                color: current.tagColor,
                fontFamily: "'DM Mono', monospace",
                fontWeight: 600,
              }}
            >
              {current.tag}
            </span>
          </div>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {active === "loan" && <AutoLoanCalculator />}
          {active === "study" && <StudyPlan />}
        </div>
      </div>
    </div>
  );
}
